import React, { useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import "./App.css";

/* ===== Utilidades ===== */
const asNumber = (v) => (v === "" || v === null ? "" : Number(v));

function eur(n) {
  if (n === "" || Number.isNaN(n) || !Number.isFinite(n)) return "-";
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
}
function pct(n) {
  if (n === "" || Number.isNaN(n) || !Number.isFinite(n)) return "-";
  return `${Number(n).toFixed(2)}%`;
}
function cuotaMensual(principal, interesAnualPct, anos) {
  if ([principal, interesAnualPct, anos].some((x) => x === "" || Number.isNaN(x))) return 0;
  const n = anos * 12;
  const r = (interesAnualPct / 100) / 12;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n)); // sistema francés
}

export default function App() {
  const logoUrl = "https://sercofutura.es/wp-content/uploads/2018/08/logo-sercofutura.png";

  const [datos, setDatos] = useState({
    principal: 150000,
    anos: 25,
    tipoConBonif: 2.3,   // A) con bonificación (banco)
    tipoSinBonif: 3.1,   // B) sin bonificación (Sercofutura)
    // Seguros Sercofutura (anual)
    primaVidaAnual: 180,
    primaHogarAnual: 120,
    // Seguros del banco (anual)
    costeVidaBancoAnual: "",
    costeHogarBancoAnual: "",
  });

  const handle = (k) => (e) => setDatos((d) => ({ ...d, [k]: asNumber(e.target.value) }));

  const {
    principal, anos, tipoConBonif, tipoSinBonif,
    primaVidaAnual, primaHogarAnual,
    costeVidaBancoAnual, costeHogarBancoAnual,
  } = datos;

  const res = useMemo(() => {
    const cuotaCon = cuotaMensual(principal, tipoConBonif, anos); // A
    const cuotaSin = cuotaMensual(principal, tipoSinBonif, anos); // B

    const segurosBanco = Number(costeVidaBancoAnual || 0) + Number(costeHogarBancoAnual || 0);
    const segurosSerco = Number(primaVidaAnual || 0) + Number(primaHogarAnual || 0);

    const totalAnualA = cuotaCon * 12 + segurosBanco;
    const totalAnualB = cuotaSin * 12 + segurosSerco;

    const ahorroAnual = totalAnualA - totalAnualB; // >0 conviene Sercofutura
    const costeBancoBreakEven = totalAnualB - cuotaCon * 12; // máx. seguros banco para empatar

    return { cuotaCon, cuotaSin, segurosBanco, segurosSerco, totalAnualA, totalAnualB, ahorroAnual, costeBancoBreakEven };
  }, [principal, anos, tipoConBonif, tipoSinBonif, primaVidaAnual, primaHogarAnual, costeVidaBancoAnual, costeHogarBancoAnual]);

  /* ===== Exportar PDF (sin imágenes externas para evitar problemas) ===== */
  const exportPDF = () => {
    const doc = new jsPDF();
    const x = 15; let y = 18;

    doc.setFontSize(18); doc.text("Sercofutura", x, y); y += 8;
    doc.setFontSize(16); doc.text("Comparativa hipoteca + seguros", x, y); y += 8;
    doc.setFontSize(12);
    const line = (t) => { doc.text(t, x, y); y += 6; };

    line(`Importe pendiente: ${eur(principal)}`);
    line(`Años pendientes: ${anos}`);
    line(`TIN con bonificación (Banco): ${pct(tipoConBonif)}`);
    line(`TIN sin bonificación (Sercofutura): ${pct(tipoSinBonif)}`);
    y += 2;

    doc.setFont(undefined, "bold"); line("A) Con bonificación + Seguros del banco"); doc.setFont(undefined, "normal");
    line(`Cuota mensual: ${eur(res.cuotaCon)}`);
    line(`Seguros banco (anual): ${eur(res.segurosBanco)}  [Vida ${eur(Number(costeVidaBancoAnual||0))} · Hogar ${eur(Number(costeHogarBancoAnual||0))}]`);
    line(`Total anual A: ${eur(res.totalAnualA)}`); y += 2;

    doc.setFont(undefined, "bold"); line("B) Sin bonificación + Seguros Sercofutura"); doc.setFont(undefined, "normal");
    line(`Cuota mensual: ${eur(res.cuotaSin)}`);
    line(`Seguros Sercofutura (anual): ${eur(res.segurosSerco)}  [Vida ${eur(Number(primaVidaAnual||0))} · Hogar ${eur(Number(primaHogarAnual||0))}]`);
    line(`Total anual B: ${eur(res.totalAnualB)}`); y += 2;

    doc.setFont(undefined, "bold"); line(`Ahorro anual estimado (A - B): ${eur(res.ahorroAnual)}`);
    doc.setFont(undefined, "normal"); line(`Punto de equilibrio (coste máx. seguros banco): ${eur(res.costeBancoBreakEven)}`);
    line(`Regla: si el banco cobra más de ${eur(Math.max(0, res.costeBancoBreakEven))} al año por sus seguros, compensa contratar con Sercofutura.`);
if (res.segurosBanco > res.costeBancoBreakEven) {
  line(`Con tus importes actuales (${eur(res.segurosBanco)} en seguros del banco), conviene Sercofutura.`);
} else if (res.segurosBanco < res.costeBancoBreakEven) {
  line(`Con tus importes actuales (${eur(res.segurosBanco)} en seguros del banco), conviene mantener la bonificación del banco.`);
} else {
  line(`Con tus importes actuales, ambas opciones quedan empatadas.`);
}


    y += 8; doc.setFontSize(10);
    line("Herramienta orientativa. No sustituye a oferta vinculante.");
    line("© Sercofutura Correduría de Seguros S.L.L.");
    line("Avda de Manoteras 30, A-212, 28050 Madrid");
    line("Tel: 91 386 26 00 · sercofutura@sercofutura.es · www.sercofutura.es");

    doc.save("comparativa-sercofutura.pdf");
  };

  return (
    <>
      <header className="app-header">
        <div className="header-inner">
          <img src={logoUrl} alt="Sercofutura" className="logo" />
          <div>
            <h1 className="title">Sercofutura · Calculadora Hipoteca + Seguros</h1>
            <p className="subtitle">Compara A) con bonificación + seguros banco vs B) sin bonificación + seguros Sercofutura</p>
          </div>
        </div>
      </header>

      <main className="container">
        {/* Entrada */}
        <section className="card">
          <h2>Datos del préstamo</h2>
          <div className="grid-2">
            <div>
              <label className="label">Cantidad pendiente (€)</label>
              <input className="input" type="number" min={1} step="1000" value={principal} onChange={handle("principal")} />
            </div>
            <div>
              <label className="label">Años pendientes</label>
              <input className="input" type="number" min={1} max={40} step="1" value={anos} onChange={handle("anos")} />
            </div>
            <div>
              <label className="label">TIN con bonificación (Banco) %</label>
              <input className="input" type="number" min={0} step="0.01" value={tipoConBonif} onChange={handle("tipoConBonif")} />
            </div>
            <div>
              <label className="label">TIN sin bonificación (Sercofutura) %</label>
              <input className="input" type="number" min={0} step="0.01" value={tipoSinBonif} onChange={handle("tipoSinBonif")} />
            </div>
          </div>

          <h2 style={{marginTop:14}}>Seguros Sercofutura (precio anual)</h2>
          <div className="grid-2">
            <div>
              <label className="label">Vida (€ / año)</label>
              <input className="input" type="number" min={0} step="1" value={primaVidaAnual} onChange={handle("primaVidaAnual")} />
            </div>
            <div>
              <label className="label">Hogar (€ / año)</label>
              <input className="input" type="number" min={0} step="1" value={primaHogarAnual} onChange={handle("primaHogarAnual")} />
            </div>
          </div>

          <h2 style={{marginTop:14}}>Seguros con el banco (precio anual)</h2>
          <div className="grid-2">
            <div>
              <label className="label">Vida (€ / año)</label>
              <input className="input" type="number" min={0} step="1" value={costeVidaBancoAnual} onChange={handle("costeVidaBancoAnual")} />
            </div>
            <div>
              <label className="label">Hogar (€ / año)</label>
              <input className="input" type="number" min={0} step="1" value={costeHogarBancoAnual} onChange={handle("costeHogarBancoAnual")} />
            </div>
          </div>

          <button className="button" onClick={exportPDF}>Descargar informe en PDF</button>
          <p className="note">Las primas de seguros son anuales. Cálculo con sistema francés (TIN nominal).</p>
        </section>

        {/* Resultados */}
        <section className="card">
          <h2>Resultados</h2>

          <div className="block">
            <h3>A) Con bonificación + seguros del banco</h3>
            <div className="row"><span>Cuota mensual (hipoteca)</span><strong>{eur(res.cuotaCon)}</strong></div>
            <div className="row"><span>Total anual (hipoteca + seguros banco)</span><strong>{eur(res.totalAnualA)}</strong></div>
          </div>

          <div className="block" style={{marginTop:12}}>
            <h3>B) Sin bonificación + seguros Sercofutura</h3>
            <div className="row"><span>Cuota mensual (hipoteca)</span><strong>{eur(res.cuotaSin)}</strong></div>
            <div className="row"><span>Total anual (hipoteca + seguros Sercofutura)</span><strong>{eur(res.totalAnualB)}</strong></div>
          </div>

          <div className={`block ${res.ahorroAnual > 0 ? 'ok' : res.ahorroAnual < 0 ? 'bad' : ''}`} style={{marginTop:12}}>
  <h3>Conclusión</h3>
  <div className="row"><span>Ahorro anual estimado (A - B)</span><strong>{eur(res.ahorroAnual)}</strong></div>
  <div className="row"><span>Punto de equilibrio (máx. seguros banco)</span><strong>{eur(res.costeBancoBreakEven)}</strong></div>

  <p className="note" style={{marginTop:10}}>
    <strong>Regla:</strong> si el banco cobra <strong>más de {eur(Math.max(0, res.costeBancoBreakEven))}</strong> al año por sus seguros (vida + hogar),
    compensa contratar con <strong>Sercofutura</strong> aunque pierdas la bonificación.
  </p>

  <p className="note">
    Con los importes que has introducido (banco: <strong>{eur(res.segurosBanco)}</strong>),
    {res.segurosBanco > res.costeBancoBreakEven ? (
      <> te conviene <strong>Sercofutura</strong>.</>
    ) : res.segurosBanco < res.costeBancoBreakEven ? (
      <> te conviene <strong>mantener la bonificación del banco</strong>.</>
    ) : (
      <> ambas opciones quedan <strong>empatadas</strong>.</>
    )}
  </p>
</div>

        </section>
      </main>

      <footer className="footer">
        © {new Date().getFullYear()} Sercofutura · Correduría de Seguros S.L.L. — Avda de Manoteras 30, A-212, 28050 Madrid · Tel: 91 386 26 00 · sercofutura@sercofutura.es · www.sercofutura.es
      </footer>
    </>
  );
}
